import { useState, useEffect, useContext } from 'react';
import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';

import config from '../../../config';
import { isNumeric, Issue } from '../../../constants';
import { useIssueNotFound } from '../../../hooks/use-issue';
import { AppContext } from '../../../App.context';

export function useGetCollection(id: string): GetCollectionResponse {
	const { graphQLClient, parseGraphQLClientError } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null>();
	const cacheTime = 1000 * 60 * (config?.app?.cache?.ttl?.getIssue || 60);
	const queryKey = ['collections', id, 'issue'];

	const { isPending, error, data } = useQuery<CollectionQuery>({
		queryKey,
		queryFn: async () =>
			graphQLClient.request(collectionQueryDocument, {
				id,
				isSlug: !isNumeric(id),
			}),
		staleTime: cacheTime,
		refetchInterval: cacheTime,
		gcTime: cacheTime,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;

		const { issue } = data;
		setIssue(issue);
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
		issue,
	};
}

interface CollectionQuery {
	issue: Issue | null;
}

interface GetCollectionResponse {
	isLoading: boolean;
	error: Error | null;
	issue?: Issue | null;
}

const collectionQueryDocument = gql`
	query GetCollection($id: ID!, $isSlug: Boolean!) {
		issue(id: $id, isSlug: $isSlug) {
			id
			name
			description
			cover
			slug
			releaseDate
			articles {
				id
				ArticleID
				thumbnail
				role
				name
				slug
				index
				description
				isFeatured
				created
				releaseDate
			}
		}
	}
`;
